import { Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FilterOperator, QuantityUnit } from "model/schema/globalTypes";
import React from "react";
import RateValidityDate from "./RateValidityDate";

const useStyles = makeStyles(theme => ({
  property: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#1E1E1E",
    minWidth: 112,
    maxWidth: 112,
    flexShrink: 0,
    paddingRight: theme.spacing(1)
  },
  value: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#464646"
  },
  row: {
    display: "flex",
    marginBottom: theme.spacing(0.5),
    alignItems: "flex-start"
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(2)
  },
  columnDivider: {
    // intentionally empty — layout handled by grid gap
  }
}));

function filterOperatorSymbol(operator: FilterOperator): string {
  switch (operator) {
    case FilterOperator.EQ:
      return "==";
    case FilterOperator.LTE:
      return "\u2264";
    case FilterOperator.GTE:
      return "\u2265";
  }
}

function quantityUnitLabel(unit: QuantityUnit): string {
  switch (unit) {
    case QuantityUnit.KG:
      return "kg";
    case QuantityUnit.LB:
      return "lb";
    case QuantityUnit.CBM:
      return "m\u00B3";
    case QuantityUnit.CFT:
      return "ft\u00B3";
    case QuantityUnit.LDM:
      return "ldm";
  }
}

interface FilterDef {
  column: string;
  displayName: string | null;
  operator: FilterOperator;
}

function FilterList({
  filters,
  testId
}: {
  filters: FilterDef[];
  testId: string;
}) {
  if (filters.length === 0) return <span>{"\u2014"}</span>;
  return (
    <>
      {filters.map((f, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <span style={{ display: "inline", color: "#888", margin: "0 2px" }}>
              {" \u00B7 "}
            </span>
          )}
          <Tooltip
            title={`${f.column} ${filterOperatorSymbol(f.operator)}`}
            arrow
            placement="top"
          >
            <span
              style={{ display: "inline", cursor: "default" }}
              data-testid={`${testId}-${i}`}
            >
              {f.displayName ?? f.column}
            </span>
          </Tooltip>
        </React.Fragment>
      ))}
    </>
  );
}

interface RateTableSummaryData {
  type: string;
  currency: string;
  description: string | null;
  validFrom: DateTime;
  validTo: DateTime;
  useMinimumProcedure: boolean | null;
  rateStructure: {
    pickupLocationFilters: FilterDef[];
    deliveryLocationFilters: FilterDef[];
    serviceOptionFilters: FilterDef[];
    primaryQuantity: {
      column: string;
      unit: QuantityUnit;
    };
  };
}

interface RateTableSummaryProps {
  rateTable: RateTableSummaryData;
}

export default function RateTableSummary({ rateTable }: RateTableSummaryProps) {
  const classes = useStyles();
  const { rateStructure } = rateTable;
  const unitLabel = quantityUnitLabel(rateStructure.primaryQuantity.unit);

  return (
    <div className={classes.summaryGrid}>
      {/* Col 1 — Table identity */}
      <div>
        <div className={classes.row}>
          <Typography className={classes.property}>Type</Typography>
          <Typography className={classes.value} data-testid="rt-detail-type">
            {rateTable.type}
          </Typography>
        </div>
        <div className={classes.row}>
          <Typography className={classes.property}>Currency</Typography>
          <Typography
            className={classes.value}
            data-testid="rt-summary-currency"
          >
            {rateTable.currency}
          </Typography>
        </div>
        <div className={classes.row}>
          <Typography className={classes.property}>Validity (UTC)</Typography>
          <Typography
            className={classes.value}
            component="div"
            data-testid="rt-summary-validity"
          >
            <RateValidityDate date={rateTable.validFrom} />
            {" \u2013 "}
            <RateValidityDate date={rateTable.validTo} />
          </Typography>
        </div>
      </div>

      {/* Col 2 — Filter dimensions */}
      <div className={classes.columnDivider}>
        <div className={classes.row}>
          <Typography className={classes.property}>Pickup Location</Typography>
          <Typography
            className={classes.value}
            component="div"
            data-testid="rt-summary-pickup"
          >
            <FilterList
              filters={rateStructure.pickupLocationFilters}
              testId="rt-summary-pickup-filter"
            />
          </Typography>
        </div>
        <div className={classes.row}>
          <Typography className={classes.property}>
            Delivery Location
          </Typography>
          <Typography
            className={classes.value}
            component="div"
            data-testid="rt-summary-delivery"
          >
            <FilterList
              filters={rateStructure.deliveryLocationFilters}
              testId="rt-summary-delivery-filter"
            />
          </Typography>
        </div>
        <div className={classes.row}>
          <Typography className={classes.property}>Service Options</Typography>
          <Typography
            className={classes.value}
            component="div"
            data-testid="rt-summary-service-options"
          >
            <FilterList
              filters={rateStructure.serviceOptionFilters}
              testId="rt-summary-service-filter"
            />
          </Typography>
        </div>
      </div>

      {/* Col 3 — Pricing axis */}
      <div className={classes.columnDivider}>
        <div className={classes.row}>
          <Typography className={classes.property}>Rate Quantity</Typography>
          <Typography
            className={classes.value}
            data-testid="rt-summary-rate-quantity"
          >
            {rateStructure.primaryQuantity.column} ({unitLabel})
          </Typography>
        </div>
        <div className={classes.row}>
          <Typography className={classes.property}>Min. Procedure</Typography>
          <Typography
            className={classes.value}
            data-testid="rt-summary-min-procedure"
          >
            {rateTable.useMinimumProcedure ? "\u2713" : "\u2014"}
          </Typography>
        </div>
      </div>
    </div>
  );
}

export type { RateTableSummaryData };
